/**
 * Downloads a file from a (typically pre-signed, time-limited) URL such as an
 * S3 signed URL. Fetches the resource as a blob and triggers a client-side
 * "save as" via a temporary <a download> element, so the filename can be
 * controlled and the browser doesn't just navigate to the URL.
 *
 * Falls back to a plain anchor-click navigation if the fetch fails (e.g. the
 * bucket's CORS policy blocks cross-origin reads but still allows direct
 * navigation/download).
 */
export async function downloadFileFromUrl(fileUrl: string, fileName?: string): Promise<void> {
  try {
    const response = await fetch(fileUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch file (status ${response.status})`);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    triggerAnchorDownload(objectUrl, fileName);
    URL.revokeObjectURL(objectUrl);
  } catch {
    // CORS or network issue — fall back to a direct navigation download.
    triggerAnchorDownload(fileUrl, fileName);
  }
}

function triggerAnchorDownload(href: string, fileName?: string) {
  const link = document.createElement('a');
  link.href = href;
  if (fileName) {
    link.download = fileName;
  }
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Derives a filename from a URL's path (ignoring query string), useful when
 * the backend doesn't supply one explicitly.
 */
export function getFileNameFromUrl(url: string, fallback = 'download'): string {
  try {
    const { pathname } = new URL(url);
    const segment = pathname.split('/').pop();
    return segment && segment.length > 0 ? decodeURIComponent(segment) : fallback;
  } catch {
    return fallback;
  }
}
